import React, { useCallback } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Avatar,
    Snackbar,
    Slide,
} from '@mui/material';
import { alpha, styled } from '@mui/material/styles';

// redux
import { useDispatch, useSelector } from '../../redux/store';
import {
    setCall,
    resetSecondaryCall,
    selectSecondaryCall,
} from '../../redux/slices/internalChat';

// socket
import { socket } from '../../contexts/SocketIo';

// components
import Iconify from '../../components/Iconify';
import { HOST_API } from '../../config';

// ----------------------------------------------------------------------

const CallWaitingRoot = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 2,
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.customShadows.z20,
    width: '100%',
    maxWidth: 400,
    border: `1px solid ${theme.palette.divider}`,
}));

const PulsingButton = styled(IconButton)(({ theme, clr }) => ({
    backgroundColor: theme.palette[clr].main,
    color: theme.palette.common.white,
    '&:hover': {
        backgroundColor: theme.palette[clr].dark,
    },
    // Simple pulse animation for the accept button
    animation: clr === 'success' ? 'pulse 2s infinite' : 'none',
    '@keyframes pulse': {
        '0%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0.7)}` },
        '70%': { boxShadow: `0 0 0 10px ${alpha(theme.palette.success.main, 0)}` },
        '100%': { boxShadow: `0 0 0 0 ${alpha(theme.palette.success.main, 0)}` }
    }
}));

// ----------------------------------------------------------------------

function TransitionLeft(props) {
    return <Slide {...props} direction="left" />;
}

export default function InternalCallWaitingModal() {
    const dispatch = useDispatch();
    const secondaryCall = useSelector(selectSecondaryCall);
    const primaryCall = useSelector((state) => state.internalChat.call);

    const open = Boolean(secondaryCall);

    const handleReject = useCallback(() => {
        if (!secondaryCall) return;

        // Dispara o evento de rejeição para a pessoa que ligou em segunda instância
        socket.emit('internal_chat:call_reject', {
            targetId: secondaryCall.partner?._id,
            chatId: secondaryCall.chatId,
            callType: secondaryCall.type,
            participantIds: secondaryCall.participantIds,
            status: 'busy'
        });

        dispatch(resetSecondaryCall());
    }, [secondaryCall, dispatch]);

    const handleAccept = useCallback(() => {
        if (!secondaryCall || !primaryCall) return;

        // 1. Desliga e Notifica a chamada PRIMÁRIA que estamos saindo
        socket.emit('internal_chat:call_hangup', {
            targetId: primaryCall.partner?._id,
            chatId: primaryCall.chatId,
            participantIds: primaryCall.participantIds,
            status: primaryCall.status === 'active' ? 'completed' : 'missed',
            callType: primaryCall.type
        });

        // 2. Manda sinal de ACEITE pro servidor sobre a SECUNDÁRIA! (Senão ninguém prepara os PeerS pra nós)
        socket.emit('internal_chat:call_accepted', {
            targetId: secondaryCall.partner?._id,
            chatId: secondaryCall.chatId,
            callType: secondaryCall.type,
            participantIds: secondaryCall.participantIds
        });

        // 3. Monta a nova chamada secundária sobre a principal no estado ACTIVE
        dispatch(setCall({
            status: 'active', // Cai na UI do InternalCall direto
            isIncoming: false, // Força a engine React a pular a tela de Incoming Call e iniciar Câmera e Sockets 
            chatId: secondaryCall.chatId,
            type: secondaryCall.type,
            partner: secondaryCall.partner,
            participantIds: secondaryCall.participantIds,
        }));

        // 4. Limpa a fila secundária
        dispatch(resetSecondaryCall());

    }, [secondaryCall, primaryCall, dispatch]);

    if (!open) return null;

    const { partner, type } = secondaryCall;
    const name = partner?.name || 'Alguém';
    const avatar = partner?.avatar;

    return (
        <Snackbar
            open={open}
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            TransitionComponent={TransitionLeft}
            sx={{ mt: 10, mr: 2 }}
        >
            <CallWaitingRoot>
                <Avatar src={avatar ? `${HOST_API}${avatar}` : ''} alt={name} sx={{ width: 48, height: 48 }}>
                    {name.charAt(0)}
                </Avatar>

                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="subtitle1" noWrap sx={{ color: 'text.primary' }}>
                        {name}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {type === 'video' ? 'Videochamada' : 'Chamada de Voz'} em espera...
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 1 }}>
                    <PulsingButton clr="error" onClick={handleReject} size="medium">
                        <Iconify icon="eva:close-fill" width={24} height={24} />
                    </PulsingButton>
                    <PulsingButton clr="success" onClick={handleAccept} size="medium">
                        <Iconify icon={type === 'video' ? 'eva:video-outline' : 'eva:phone-call-fill'} width={24} height={24} />
                    </PulsingButton>
                </Box>
            </CallWaitingRoot>
        </Snackbar>
    );
}
